const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL ||
  process.env.REACT_APP_API_BASE_URL || 
  process.env.VITE_API_BASE_URL ||
  'http://localhost:3001';

// Log the API URL being used (helpful for debugging)
if (typeof window !== 'undefined') {
  console.log('API Base URL:', API_BASE_URL);
}
  
const getAuthToken = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/dev/token`);
    
    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');
    
    // Read response body once (can only be read once)
    let responseData;
    let responseText;
    
    if (!isJson) {
      responseText = await response.text();
    } else {
      responseData = await response.json();
    }
    
    if (!response.ok) {
      if (isJson && responseData) {
        throw new Error(responseData.message || `HTTP ${response.status}: ${response.statusText}`);
      } else {
        throw new Error(`Server returned HTML instead of JSON. This usually means the backend server is not running or the endpoint doesn't exist. Status: ${response.status}. Response: ${responseText ? responseText.substring(0, 100) : 'N/A'}...`);
      }
    }
    
    if (!isJson || !responseData) {
      throw new Error(`Expected JSON but received: ${contentType || 'unknown'}. Response: ${responseText ? responseText.substring(0, 100) : 'N/A'}...`);
    }
    
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('authToken', responseData.token);
    }
    return responseData.token;
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return null;
  }
};

async function apiGet(path, options = {}) {
  const token = await getAuthToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  
  // Check content type before parsing
  const contentType = res.headers.get('content-type');
  const isJson = contentType && contentType.includes('application/json');
  
  // Read response body once (can only be read once)
  let responseData;
  let responseText;
  
  if (!isJson) {
    responseText = await res.text();
  } else {
    responseData = await res.json();
  }
  
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    if (isJson && responseData) {
      if (responseData.message || responseData.error) {
        message = responseData.message || responseData.error;
      }
    } else {
      // Server returned HTML or other non-JSON response
      if (res.status === 404) {
        message = `Endpoint not found: ${path}. The API server may not have this route configured. Make sure the backend server is running on ${API_BASE_URL}`;
      } else if (res.status === 500) {
        message = `Server error: The API server encountered an internal error.`;
      } else {
        message = `HTTP ${res.status}: ${res.statusText}. Server returned non-JSON response (${contentType || 'unknown'}).`;
      }
      if (responseText) {
        message += ` Response preview: ${responseText.substring(0, 150)}...`;
      }
    }
    throw new Error(message);
  }
  
  // Only return JSON if content type indicates JSON
  if (!isJson) {
    throw new Error(`Expected JSON response but received: ${contentType || 'unknown content type'}. Response: ${responseText.substring(0, 100)}...`);
  }
  
  return responseData;
}

async function apiPost(path, body, options = {}) {
  const token = await getAuthToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  
  // Check content type before parsing
  const contentType = res.headers.get('content-type');
  const isJson = contentType && contentType.includes('application/json');
  
  // Read response body once (can only be read once)
  let responseData;
  let responseText;
  
  if (!isJson) {
    responseText = await res.text();
  } else {
    responseData = await res.json();
  }
  
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    if (isJson && responseData) {
      if (responseData.message || responseData.error) {
        message = responseData.message || responseData.error;
      }
    } else {
      // Server returned HTML or other non-JSON response
      if (res.status === 404) {
        message = `Endpoint not found: ${path}. The API server may not have this route configured. Make sure the backend server is running on ${API_BASE_URL}`;
      } else if (res.status === 500) {
        message = `Server error: The API server encountered an internal error.`;
      } else {
        message = `HTTP ${res.status}: ${res.statusText}. Server returned non-JSON response (${contentType || 'unknown'}).`;
      }
      if (responseText) {
        message += ` Response preview: ${responseText.substring(0, 150)}...`;
      }
    }
    throw new Error(message);
  }
  
  // Only return JSON if content type indicates JSON
  if (!isJson) {
    throw new Error(`Expected JSON response but received: ${contentType || 'unknown content type'}. Response: ${responseText.substring(0, 100)}...`);
  }
  
  return responseData;
}

export { API_BASE_URL, apiGet, apiPost, getAuthToken };

